//@flow

import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import getter from 'dibs-object-getter';
import {
    TPN_TIER_MEMBER,
    TPN_TIER_BRONZE,
    TPN_TIER_SILVER,
    TPN_TIER_GOLD,
    TPN_TIER_PLATINUM,
} from 'dibs-trade-rewards-progress/exports/tpnStatusCodes';

import { TierDetails } from './TierDetails';

import styles from './BenefitsChartMobile.scss';

import type { BenefitsChartMobile_tradeFirm as TradeFirmType } from './__generated__/BenefitsChartMobile_tradeFirm.graphql';
import type { BenefitsChartMobile_viewer as ViewerType } from './__generated__/BenefitsChartMobile_viewer.graphql';

type BenfitsChartMobileProps = {|
    tradeFirm: TradeFirmType,
    viewer: ViewerType,
|};

type BenefitsChartMobileState = {|
    selectedTpnTier: ?string,
|};

export class BenefitsChartMobileComponent extends React.Component<
    BenfitsChartMobileProps,
    BenefitsChartMobileState
> {
    constructor(props: BenfitsChartMobileProps) {
        super(props);

        this.state = {
            selectedTpnTier: this.getFirmTpnTier() || TPN_TIER_MEMBER,
        };
    }

    getFirmTpnTier() {
        return (
            getter(this.props)
                .at('tradeFirm')
                .at('rewards')
                .at('currentProgram')
                .at('programInfo')
                .value('code') || ''
        );
    }

    setSelectedTpnTier = (code: ?string) => {
        this.setState({ selectedTpnTier: code });
    };

    render() {
        const { viewer } = this.props;
        const { selectedTpnTier } = this.state;
        const tpnRoles = getter(viewer).value('tradeProgramsQuery') || [];
        const firmTpnTier = this.getFirmTpnTier();

        const selectedRoleIndex = tpnRoles.findIndex(
            role => getter(role).value('code') === selectedTpnTier
        );
        const selectedRole = tpnRoles[selectedRoleIndex];
        const selectedRoleDisplayName = getter(selectedRole).value('displayName');
        const minPoints = getter(selectedRole).value('minPoints') || 0;
        const maxPoints = getter(selectedRole).value('maxPoints') || 0;

        const headerClass = classnames(styles.chartHeader, {
            [styles.bottomBorderMember]: selectedTpnTier === TPN_TIER_MEMBER,
            [styles.bottomBorderBronze]: selectedTpnTier === TPN_TIER_BRONZE,
            [styles.bottomBorderSilver]: selectedTpnTier === TPN_TIER_SILVER,
            [styles.bottomBorderGold]: selectedTpnTier === TPN_TIER_GOLD,
            [styles.bottomBorderPlatinum]: selectedTpnTier === TPN_TIER_PLATINUM,
        });

        const currentTierIndicator = (
            <div className={styles.currentTierIndicator} data-tn="current-status-indicator">
                <FormattedMessage
                    id="abt.loyaltyModules.BenfitsChartMobile.currentTierIndicator"
                    defaultMessage="Your Status"
                />
            </div>
        );

        return (
            <div className={styles.container}>
                <div className={styles.chart}>
                    <div className={styles.chartTitle}>
                        <FormattedMessage
                            id="abt.loyaltyModules.BenfitsChartMobile.chartTitle"
                            defaultMessage="Trade Rewards Tiers"
                        />
                    </div>
                    <div className={headerClass}>
                        {tpnRoles.filter(Boolean).map(role => {
                            const { code, iconUri } = role;
                            const isSelectedTier = code === selectedTpnTier;
                            const isFirmStatus = code === firmTpnTier;
                            const isMemberCell = code === TPN_TIER_MEMBER;
                            const cellClass = classnames(styles.headerCell, {
                                [styles.memberHeaderCell]: isMemberCell,
                                [styles.selectedMemberCell]: isMemberCell && isSelectedTier,
                                [styles.bronzeBgColor]: code === TPN_TIER_BRONZE && isSelectedTier,
                                [styles.silverBgColor]: code === TPN_TIER_SILVER && isSelectedTier,
                                [styles.goldBgColor]: code === TPN_TIER_GOLD && isSelectedTier,
                                [styles.platinumBgColor]:
                                    code === TPN_TIER_PLATINUM && isSelectedTier,
                            });

                            return (
                                <div
                                    className={cellClass}
                                    data-tn={`header-cell-${code || ''}`}
                                    key={code}
                                    onClick={() => this.setSelectedTpnTier(code)}
                                >
                                    {isFirmStatus && currentTierIndicator}
                                    {iconUri && (
                                        <div className={styles.tpnStatusIconContainer}>
                                            <img className={styles.tpnStatusIcon} src={iconUri} />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <div className={styles.tierDetailsContainer}>
                        <div className={styles.tierDetailsWrapper}>
                            <TierDetails
                                code={selectedTpnTier}
                                displayName={selectedRoleDisplayName}
                                minimumAnnualSpend={minPoints}
                                maxAnnualSpend={maxPoints}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.disclaimer}>
                    <FormattedMessage
                        id="abt.loyaltyModules.BenfitsChartMobile.disclaimer"
                        defaultMessage="*Only firms with published profiles are eligible. Inclusion is subject to 1stdibs’ discretion."
                    />
                </div>
            </div>
        );
    }
}

export const BenefitsChartMobile = createFragmentContainer(BenefitsChartMobileComponent, {
    tradeFirm: graphql`
        fragment BenefitsChartMobile_tradeFirm on TradeFirmType {
            rewards {
                currentProgram {
                    programInfo {
                        code
                    }
                }
            }
        }
    `,
    viewer: graphql`
        fragment BenefitsChartMobile_viewer on Viewer {
            tradeProgramsQuery(programCodes: ["MEMBER", "BRONZE", "SILVER", "GOLD", "PLATINUM"]) {
                code
                displayName
                iconUri
                maxPoints
                minPoints
            }
        }
    `,
});
